// src/services/userService.js
/**
 * User Service
 * 
 * Handles all API communication for User Management.
 * Includes CRUD operations, password management, status changes, and search.
 */

import axios from '../config/axios';
import { API_CONFIG } from '../components/auth/constants';

/**
 * Get paginated list of users with filtering and sorting
 * 
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (0-indexed)
 * @param {number} params.size - Page size
 * @param {string} params.sortBy - Sort field (username, email, fullName, role, status, createdAt)
 * @param {string} params.sortDirection - Sort direction (ASC, DESC)
 * @param {string} params.search - Search query (searches username, fullName, email)
 * @param {string} params.role - Filter by role
 * @param {string} params.status - Filter by status
 * @returns {Promise<Object>} Paginated user list
 * 
 * Response format:
 * {
 *   content: [...users],
 *   page: 0,
 *   size: 10,
 *   totalElements: 100,
 *   totalPages: 10,
 *   first: true,
 *   last: false
 * }
 */
export const getUsers = async (params = {}) => {
  try {
    const {
      page = 0,
      size = 10,
      sortBy = 'username',
      sortDirection = 'ASC',
      search = '',
      role = '',
      status = '',
    } = params;

    const response = await axios.get(API_CONFIG.ENDPOINTS.USERS.BASE, {
      params: {
        page,
        size,
        sortBy,
        sortDirection,
        search: search || undefined, // Don't send empty string
        role: role || undefined,
        status: status || undefined,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Get user by ID
 * 
 * @param {number} id - User ID
 * @returns {Promise<Object>} User details
 */
export const getUserById = async (id) => {
  try {
    const response = await axios.get(API_CONFIG.ENDPOINTS.USERS.BY_ID(id));
    return response.data;
  } catch (error) {
    console.error(`Error fetching user ${id}:`, error);
    throw error;
  }
};

/**
 * Create new user
 * 
 * @param {Object} userData - User data
 * @param {string} userData.username - Username (required, 3-50 chars, unique)
 * @param {string} userData.email - Email (required, valid email, unique)
 * @param {string} userData.password - Password (required, min 8 chars)
 * @param {string} userData.fullName - Full name (required)
 * @param {string} userData.phone - Phone number (optional)
 * @param {string} userData.role - Role (required)
 * @param {string} userData.status - Status (ACTIVE, INACTIVE, SUSPENDED)
 * @param {boolean} userData.canAccessWeb - Can access web application
 * @param {boolean} userData.canAccessMobile - Can access mobile application
 * @returns {Promise<Object>} Created user
 */
export const createUser = async (userData) => {
  try {
    const response = await axios.post(API_CONFIG.ENDPOINTS.USERS.BASE, userData);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Update existing user
 * 
 * @param {number} id - User ID
 * @param {Object} userData - User data (same as create, password optional)
 * @returns {Promise<Object>} Updated user
 */
export const updateUser = async (id, userData) => {
  try {
    const response = await axios.put(API_CONFIG.ENDPOINTS.USERS.BY_ID(id), userData);
    return response.data;
  } catch (error) {
    console.error(`Error updating user ${id}:`, error);
    throw error;
  }
};

/**
 * Delete user (soft delete)
 * 
 * @param {number} id - User ID
 * @returns {Promise<void>}
 */
export const deleteUser = async (id) => {
  try {
    await axios.delete(API_CONFIG.ENDPOINTS.USERS.BY_ID(id));
  } catch (error) {
    console.error(`Error deleting user ${id}:`, error);
    throw error;
  }
};

/**
 * Activate user
 * 
 * @param {number} id - User ID
 * @returns {Promise<Object>} Updated user
 */
export const activateUser = async (id) => {
  try {
    const response = await axios.patch(API_CONFIG.ENDPOINTS.USERS.ACTIVATE(id));
    return response.data;
  } catch (error) {
    console.error(`Error activating user ${id}:`, error);
    throw error;
  }
};

/**
 * Deactivate user
 * 
 * @param {number} id - User ID
 * @returns {Promise<Object>} Updated user
 */
export const deactivateUser = async (id) => {
  try {
    const response = await axios.patch(API_CONFIG.ENDPOINTS.USERS.DEACTIVATE(id));
    return response.data;
  } catch (error) {
    console.error(`Error deactivating user ${id}:`, error);
    throw error;
  }
};

/**
 * Suspend user
 * 
 * @param {number} id - User ID
 * @returns {Promise<Object>} Updated user
 */
export const suspendUser = async (id) => {
  try {
    const response = await axios.patch(API_CONFIG.ENDPOINTS.USERS.SUSPEND(id));
    return response.data;
  } catch (error) {
    console.error(`Error suspending user ${id}:`, error);
    throw error;
  }
};

/**
 * Unlock user account
 * 
 * @param {number} id - User ID
 * @returns {Promise<Object>} Updated user
 */
export const unlockUser = async (id) => {
  try {
    const response = await axios.patch(API_CONFIG.ENDPOINTS.USERS.UNLOCK(id));
    return response.data;
  } catch (error) {
    console.error(`Error unlocking user ${id}:`, error);
    throw error;
  }
};

/**
 * Reset user password (admin function)
 * 
 * @param {number} id - User ID
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Result message
 */
export const resetUserPassword = async (id, newPassword) => {
  try {
    const response = await axios.post(API_CONFIG.ENDPOINTS.USERS.RESET_PASSWORD(id), {
      newPassword,
    });
    return response.data;
  } catch (error) {
    console.error(`Error resetting password for user ${id}:`, error);
    throw error;
  }
};

/**
 * Change user password (user function)
 * 
 * @param {number} id - User ID
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Result message
 */
export const changeUserPassword = async (id, currentPassword, newPassword) => {
  try {
    const response = await axios.post(API_CONFIG.ENDPOINTS.USERS.CHANGE_PASSWORD(id), {
      currentPassword,
      newPassword,
    });
    return response.data;
  } catch (error) {
    console.error(`Error changing password for user ${id}:`, error);
    throw error;
  }
};

/**
 * Search users
 * 
 * @param {Object} searchParams - Search parameters
 * @param {string} searchParams.query - Search query
 * @param {string} searchParams.role - Filter by role
 * @param {string} searchParams.status - Filter by status
 * @param {number} searchParams.page - Page number
 * @param {number} searchParams.size - Page size
 * @returns {Promise<Object>} Search results
 */
export const searchUsers = async (searchParams) => {
  try {
    const response = await axios.get(API_CONFIG.ENDPOINTS.USERS.SEARCH, {
      params: searchParams,
    });
    return response.data;
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
};

/**
 * Get user statistics
 * 
 * @returns {Promise<Object>} User statistics
 * 
 * Response format:
 * {
 *   totalUsers: 100,
 *   activeUsers: 85,
 *   inactiveUsers: 10,
 *   suspendedUsers: 5,
 *   usersByRole: {
 *     ADMINISTRATOR: 2,
 *     SALES_MANAGER: 5,
 *     ...
 *   }
 * }
 */
export const getUserStatistics = async () => {
  try {
    const response = await axios.get(API_CONFIG.ENDPOINTS.USERS.STATISTICS);
    return response.data;
  } catch (error) {
    console.error('Error fetching user statistics:', error);
    throw error;
  }
};

/**
 * Bulk operations
 */

/**
 * Bulk activate users
 * 
 * @param {number[]} userIds - Array of user IDs
 * @returns {Promise<Object>} Result
 */
export const bulkActivateUsers = async (userIds) => {
  try {
    const response = await axios.post(`${API_CONFIG.ENDPOINTS.USERS.BASE}/bulk-activate`, {
      userIds,
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk activating users:', error);
    throw error;
  }
};

/**
 * Bulk deactivate users
 * 
 * @param {number[]} userIds - Array of user IDs
 * @returns {Promise<Object>} Result
 */
export const bulkDeactivateUsers = async (userIds) => {
  try {
    const response = await axios.post(`${API_CONFIG.ENDPOINTS.USERS.BASE}/bulk-deactivate`, {
      userIds,
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk deactivating users:', error);
    throw error;
  }
};

/**
 * Bulk delete users
 * 
 * @param {number[]} userIds - Array of user IDs
 * @returns {Promise<Object>} Result
 */
export const bulkDeleteUsers = async (userIds) => {
  try {
    const response = await axios.post(`${API_CONFIG.ENDPOINTS.USERS.BASE}/bulk-delete`, {
      userIds,
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk deleting users:', error);
    throw error;
  }
};

/**
 * Export users to Excel
 * 
 * @param {Object} filters - Export filters (same as getUsers params)
 * @returns {Promise<Blob>} Excel file blob
 */
export const exportUsersToExcel = async (filters = {}) => {
  try {
    const response = await axios.get(`${API_CONFIG.ENDPOINTS.USERS.BASE}/export/excel`, {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting users to Excel:', error);
    throw error;
  }
};

/**
 * Export users to PDF
 * 
 * @param {Object} filters - Export filters (same as getUsers params)
 * @returns {Promise<Blob>} PDF file blob
 */
export const exportUsersToPDF = async (filters = {}) => {
  try {
    const response = await axios.get(`${API_CONFIG.ENDPOINTS.USERS.BASE}/export/pdf`, {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting users to PDF:', error);
    throw error;
  }
};

/**
 * Utility function to download blob as file
 * 
 * @param {Blob} blob - File blob
 * @param {string} filename - Filename
 */
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Export users to Excel and download
 * 
 * @param {Object} filters - Export filters
 * @param {string} filename - Filename (default: users_export.xlsx)
 */
export const downloadUsersExcel = async (filters = {}, filename = 'users_export.xlsx') => {
  try {
    const blob = await exportUsersToExcel(filters);
    downloadFile(blob, filename);
  } catch (error) {
    console.error('Error downloading users Excel:', error);
    throw error;
  }
};

/**
 * Export users to PDF and download
 * 
 * @param {Object} filters - Export filters
 * @param {string} filename - Filename (default: users_export.pdf)
 */
export const downloadUsersPDF = async (filters = {}, filename = 'users_export.pdf') => {
  try {
    const blob = await exportUsersToPDF(filters);
    downloadFile(blob, filename);
  } catch (error) {
    console.error('Error downloading users PDF:', error);
    throw error;
  }
};

/**
 * Validation helpers
 */

/**
 * Check if username is available
 * 
 * @param {string} username - Username to check
 * @param {number} excludeUserId - User ID to exclude from check (for updates)
 * @returns {Promise<boolean>} True if available
 */
export const isUsernameAvailable = async (username, excludeUserId = null) => {
  try {
    const response = await axios.get(`${API_CONFIG.ENDPOINTS.USERS.BASE}/check-username`, {
      params: {
        username,
        excludeUserId,
      },
    });
    return response.data.available;
  } catch (error) {
    console.error('Error checking username availability:', error);
    return false;
  }
};

/**
 * Check if email is available
 * 
 * @param {string} email - Email to check
 * @param {number} excludeUserId - User ID to exclude from check (for updates)
 * @returns {Promise<boolean>} True if available
 */
export const isEmailAvailable = async (email, excludeUserId = null) => {
  try {
    const response = await axios.get(`${API_CONFIG.ENDPOINTS.USERS.BASE}/check-email`, {
      params: {
        email,
        excludeUserId,
      },
    });
    return response.data.available;
  } catch (error) {
    console.error('Error checking email availability:', error);
    return false;
  }
};

/**
 * Default export
 */
export default {
  // CRUD operations
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,

  // Status management
  activateUser,
  deactivateUser,
  suspendUser,
  unlockUser,

  // Password management
  resetUserPassword,
  changeUserPassword,

  // Search and filter
  searchUsers,
  getUserStatistics,

  // Bulk operations
  bulkActivateUsers,
  bulkDeactivateUsers,
  bulkDeleteUsers,

  // Export
  exportUsersToExcel,
  exportUsersToPDF,
  downloadUsersExcel,
  downloadUsersPDF,

  // Validation
  isUsernameAvailable,
  isEmailAvailable,
};